// "Rapordan doldur" modalı — Geçmiş Rapor Aktarımı (Task 14, tasarım review 16A).
//
// Görsel kabuk `GenerateConfirmModal` desenine yakın (ortalanmış kart, backdrop tıkla-kapat);
// a11y `ConfirmDialog`/`UnmappedDrawer` deseni (role=dialog, aria-modal, Esc, odak tuzağı) —
// bu ekranda YENİ bir dialog türü olduğu için 15A axe critical=0 şartını burada da tutar.
//
// Akış: `ImportDropzone` (Task 13'ün ortak sarmalayıcısı, ≤2 dosya) → seçilen dosyalar
// aynı gövde değilse ikincisi anında reddedilir (`pairNote`) → "Aktarımı başlat" →
// her dosya `uploadImportFile` ile yüklenir (aria-busy) → `pairFilesForFill` birincil
// bayrağını K16'ya göre kurar → `fillProjectFromReport` → `onQueued()`.

import { useEffect, useId, useRef, useState } from "react";
import type { ReportImportFile } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { fillProjectFromReport } from "../../api/report-import";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  groupFilesByStem,
  pairFilesForFill,
  uploadImportFile,
} from "../../lib/report-import-ui";
import { ImportDropzone } from "../report-import/ImportDropzone";

export interface FillFromReportModalProps {
  projectId: string;
  open: boolean;
  client: string;
  period: string;
  framework: string;
  onClose: () => void;
  /** Aktarım kuyruğa alındı — parent modalı kapatır + bilgi satırı gösterir. */
  onQueued: () => void;
}

interface FileEntry {
  file: File;
  progress: number;
  status: "idle" | "uploading" | "done" | "failed";
}

function extOf(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx === -1 ? "" : fileName.slice(idx + 1).toLowerCase();
}

export function FillFromReportModal(props: FillFromReportModalProps): JSX.Element | null {
  const { projectId, open, client, period, framework, onClose, onQueued } = props;
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [pairError, setPairError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal her açılışta temiz state ile başlar.
  useEffect(() => {
    if (!open) return;
    setEntries([]);
    setPairError(false);
    setBusy(false);
    setError(null);
  }, [open]);

  // Esc + odak tuzağı (ConfirmDialog/UnmappedDrawer ile aynı desen).
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function addFiles(incoming: File[]): void {
    setEntries((prev) => {
      const merged = [...prev.map((e) => e.file), ...incoming];
      if (merged.length <= 1) {
        setPairError(false);
        return merged.map((file) => ({ file, progress: 0, status: "idle" as const }));
      }
      const groups = groupFilesByStem(merged);
      // Aynı gövde + aynı tür (iki pdf ya da iki docx) — FilesSchema'nın "aynı
      // türden iki dosya olamaz" kuralı; `groupFilesByStem` tek grup üretir
      // (ikincisi birincisinin üzerine yazar) ama biz burada erken reddederiz.
      const sameKindDuplicate =
        merged.length === 2 && extOf(merged[0]!.name) === extOf(merged[1]!.name);
      if (groups.length > 1 || sameKindDuplicate) {
        // Farklı gövde ya da aynı tür ikinci dosya — yalnız ilkini tut, ikincisi
        // reddedilir (16A: "reject the second with pairNote"); kullanıcı "Kaldır"
        // ile ilkini çıkarıp yeniden deneyebilir.
        setPairError(true);
        return [merged[0]!].map((file) => ({ file, progress: 0, status: "idle" as const }));
      }
      setPairError(false);
      return merged.slice(0, 2).map((file) => ({ file, progress: 0, status: "idle" as const }));
    });
    setError(null);
  }

  function removeFile(file: File): void {
    setEntries((prev) => prev.filter((e) => e.file !== file));
    setPairError(false);
    setError(null);
  }

  async function handleStart(): Promise<void> {
    if (entries.length === 0 || pairError || busy) return;
    setBusy(true);
    setError(null);
    try {
      const uploaded: ReportImportFile[] = [];
      for (const entry of entries) {
        setEntries((prev) =>
          prev.map((e) => (e.file === entry.file ? { ...e, status: "uploading", progress: 0 } : e)),
        );
        try {
          const result = await uploadImportFile(entry.file, (pct) => {
            setEntries((prev) =>
              prev.map((e) => (e.file === entry.file ? { ...e, progress: pct } : e)),
            );
          });
          uploaded.push(result);
          setEntries((prev) =>
            prev.map((e) => (e.file === entry.file ? { ...e, status: "done", progress: 100 } : e)),
          );
        } catch {
          setEntries((prev) =>
            prev.map((e) => (e.file === entry.file ? { ...e, status: "failed" } : e)),
          );
          setError(fillTemplate(IMPORT_COPY.uploadFailed, { file: entry.file.name }));
          setBusy(false);
          return;
        }
      }
      const paired = pairFilesForFill(uploaded);
      if ("error" in paired) {
        setError(IMPORT_COPY.pairNote);
        setBusy(false);
        return;
      }
      await fillProjectFromReport({ projectId, files: paired.files });
      onQueued();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Aktarım başlatılamadı");
    } finally {
      setBusy(false);
    }
  }

  const canStart = entries.length > 0 && !pairError && !busy;
  const showPairNote = entries.length === 2 && !pairError;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        tabIndex={-1}
        className="rounded-xl p-6 max-w-md w-full mx-4"
        style={{ background: "var(--color-surface)", color: "var(--color-text)", outline: "none" }}
      >
        <h2
          id={titleId}
          className="mb-1"
          style={{ fontFamily: "var(--font-display)", fontSize: "1.15rem" }}
        >
          {fillTemplate(IMPORT_COPY.fillModalTitle, { client, period })}
        </h2>
        <p className="mb-4 text-sm" style={{ color: "var(--color-text-2)" }}>
          {client} · {period} · {framework}
        </p>

        <ImportDropzone onFiles={addFiles} disabled={busy} maxFiles={2} />

        {entries.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1.5" style={{ fontSize: "0.82rem" }}>
            {entries.map((e) => (
              <li
                key={e.file.name}
                className="flex items-center justify-between gap-2"
                style={{ color: "var(--color-text-2)" }}
              >
                <span className="truncate">{e.file.name}</span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span style={{ color: "var(--color-text-2)" }}>
                    {e.status === "uploading"
                      ? fillTemplate(IMPORT_COPY.rowUploading, { n: e.progress })
                      : e.status === "done"
                        ? IMPORT_COPY.fileUploaded
                        : e.status === "failed"
                          ? fillTemplate(IMPORT_COPY.uploadFailed, { file: e.file.name }).split(
                              " — ",
                            )[0]
                          : null}
                  </span>
                  {!busy && (
                    <button
                      type="button"
                      onClick={() => removeFile(e.file)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "var(--color-text-2)",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      {IMPORT_COPY.fileRemove}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {showPairNote && (
          <p className="mt-2 text-xs" style={{ color: "var(--color-text-2)" }}>
            {IMPORT_COPY.pairNote}
          </p>
        )}
        {pairError && (
          <p className="mt-2 text-xs" style={{ color: "var(--color-text-2)" }}>
            {IMPORT_COPY.pairNote}
          </p>
        )}
        {error && (
          <div
            className="rounded-md p-3 mt-3 text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-5">
          <span style={{ fontSize: "0.78rem", color: "var(--color-text-2)" }}>
            {IMPORT_COPY.fillModalBody}
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm disabled:opacity-50"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-2)",
              }}
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={() => void handleStart()}
              disabled={!canStart}
              aria-busy={busy || undefined}
              className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "var(--color-primary)",
                color: "var(--color-surface)",
                fontWeight: 600,
              }}
            >
              {busy ? "…" : IMPORT_COPY.fillStart}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
